import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Component, ElementRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { BetterBuysConstants } from '@app/better-buys/constants/better-buys-constants.enum';
import { ListAnalyticsHelperService } from '@app/lists/services/list-analytics-helper.service';
import {
  IonInput,
  ViewDidEnter,
  ViewWillEnter,
  ViewWillLeave,
} from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { PanAppState, PlatformService } from '@panamax/app-state';
import { FEATURES } from '@shared/constants/splitio-features';
import { BetterBuyDeclineEvent } from '@shared/models/better-buy-card.model';
import { DownloadListOptions, ListConstants } from '@usf/ngrx-list';
import { combineLatest, filter, mergeMap, of, Subscription, take } from 'rxjs';
import { BetterBuysService } from '../../../better-buys/services/better-buys.service';
import { Crumb } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import {
  ListTypes,
  MSL_MAX_SELECTABLE_PRODUCTS_FOR_COMPARISON,
} from '../../../shared/constants/lists-constants';
import { UsfProductCardModeEnum } from '../../../shared/constants/usf-product-card-mode.enum';
import { Product } from '../../../shared/models/product.model';
import {
  EllipsisClickData,
  ProductClickData,
} from '../../../shared/models/usf-product-card.model';
import { ListHotkeyService } from '../../../shared/services/hotkey/list-hotkey.service';
import { MasterListProductCardService } from '../../../shared/services/product-card/master-list-product-card.service';
import { ListsOverlayService } from '../../services/lists-overlay.service';
import {
  ItemTypes,
  ProductRow,
} from '../../shared/list-detail-management/model/list-detail-management-view.model';
import { ListDetailManagementService } from '../../shared/list-detail-management/services/list-detail-management.service';
import { MasterListViewModel } from './model/master-list-view.model';
import { createMasterListItemProductKey } from './selectors/helpers/master-list.selectors.helpers';
import { MasterListService } from './services/master-list.service';
import { ProductCompareEnum } from '@shared/constants/product-compare.enum';
import { LoadingState } from '@usf/ngrx-product';
import { ListDownloadContainerComponent } from '@app/lists/shared/list-download-container/list-download-container.component';
import { ListPrintContainerComponent } from '@app/lists/shared/list-print-container/list-print-container.component';
import { ListProductCardService } from '@shared/services/product-card/list-product-card.service';

@Component({
  selector: 'app-master-list',
  templateUrl: './master-list.page.html',
  styleUrls: ['../lists-pages-common-styles.scss', './master-list.page.scss'],
})
export class MasterListPage
  implements ViewWillEnter, ViewDidEnter, ViewWillLeave
{
  // Children components
  @ViewChild(CdkVirtualScrollViewport) virtualScroll: CdkVirtualScrollViewport;
  @ViewChild('sidebar') sidebar: ElementRef;
  @ViewChild(ListDownloadContainerComponent)
  listDownloadModal: ListDownloadContainerComponent;
  @ViewChild(ListPrintContainerComponent)
  listPrintModal: ListPrintContainerComponent;

  // Constants
  mode: UsfProductCardModeEnum = UsfProductCardModeEnum.listDetail;
  readonly ItemTypes = ItemTypes;
  readonly ListTypes = ListTypes;
  readonly maxSelectableProductsForComparison =
    MSL_MAX_SELECTABLE_PRODUCTS_FOR_COMPARISON;

  // Component variables
  viewModel: MasterListViewModel;
  listKey = this.listDetailManagementService.getListIdFromRoute(
    this.route.snapshot,
  );
  sidebarTopPosition: number;
  searchKey = '';
  breadcrumbs: Crumb[];
  showTopButton = false;
  isLoading: Boolean = true;
  copyListEnabled: boolean;
  isRedirectingToComparePage = false;
  // Subscriptions
  masterListViewModelSub$: Subscription = new Subscription();
  virtualScrollSub$: Subscription = new Subscription();
  currentQuantityInput: IonInput = undefined;

  constructor(
    private listDetailManagementService: ListDetailManagementService,
    private masterListProductCardService: MasterListProductCardService,
    public listsOverlayService: ListsOverlayService,
    private route: ActivatedRoute,
    private router: Router,
    readonly panAppState: PanAppState,
    public translateService: TranslateService,
    readonly platformService: PlatformService,
    private masterListService: MasterListService,
    private hotKeyService: ListHotkeyService,
    private listAnalyticsHelperService: ListAnalyticsHelperService,
    private betterBuysService: BetterBuysService,
    private listProductCardService: ListProductCardService,
  ) {}

  ionViewWillEnter() {
    this.currentQuantityInput = undefined;
    this.isRedirectingToComparePage = false;
    this.updateListForUserPreferences();
    this.setPageTitle();
    this.setVirtualScrollSub();
    this.setBreadcrumbs();
    this.getViewModel();
    this.panAppState
      .featureEnabled(FEATURES.split_global_copy_list.name)
      .then(isEnabled => (this.copyListEnabled = isEnabled));
  }

  ionViewDidEnter() {
    this.trackAndTraceMasterListLoad();
  }

  trackAndTraceMasterListLoad() {
    this.masterListService
      .selectMasterListViewModel(
        Number(this.listKey.listId),
        this.platformService.platformType,
      )
      .pipe(
        filter(
          (masterListViewModel: MasterListViewModel) =>
            masterListViewModel.productLoadingState === LoadingState.loaded ||
            masterListViewModel.productLoadingState === LoadingState.error,
        ),
        take(1),
      )
      .subscribe((masterListViewModel: MasterListViewModel) => {
        if (masterListViewModel.productLoadingState === LoadingState.loaded) {
          this.handleSuccessfulPageLoad(masterListViewModel);
        }
        if (masterListViewModel.productLoadingState === LoadingState.error) {
          this.handleErrorForPageLoad(masterListViewModel);
        }
      });
  }

  handleSuccessfulPageLoad(masterListViewModel: MasterListViewModel) {
    this.listAnalyticsHelperService.masterListsPageLoad(
      this.listKey.listTypeId + '-' + this.listKey.listId,
      masterListViewModel.items,
    );
    this.listDetailManagementService.displayListDetailsSuccessTrace(
      ListConstants.masterListTypeId,
      String(masterListViewModel.list.listId),
      masterListViewModel.list.primaryCount,
    );
    this.isLoading = false;
  }

  handleErrorForPageLoad(masterListViewModel: MasterListViewModel) {
    this.listDetailManagementService.displayListDetailsFailureTrace(
      ListConstants.masterListTypeId,
      String(masterListViewModel.list.listId),
      masterListViewModel.list.primaryCount,
    );
    this.listsOverlayService.showListLoadingErrorToast();
    this.routeTo('/lists');
  }

  updateListForUserPreferences() {
    this.listDetailManagementService.updateRecentlyViewedUserPreferences(
      this.listKey?.listTypeId + '-' + this.listKey?.listId,
    );
  }

  setPageTitle() {
    this.panAppState.setPageTitle(
      this.translateService.instant('i18n.lists.myLists'),
    );
  }

  setVirtualScrollSub() {
    this.virtualScrollSub$ = this.virtualScroll
      ?.elementScrolled()
      ?.subscribe(scrollEvent => {
        this.showTopButton =
          this.virtualScroll.measureScrollOffset('top') > 5 ? true : false;
      });
  }

  setBreadcrumbs() {
    this.breadcrumbs = [
      {
        label: 'i18n.breadcrumbs.home',
        url: '/desktop/home',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.myLists',
        url: '/desktop/lists',
        active: false,
      },
      {
        label: 'i18n.breadcrumbs.masterList',
        url: '',
        active: true,
      },
    ];
  }

  getViewModel() {
    this.masterListViewModelSub$ = this.masterListService
      .selectMasterListViewModel(
        Number(this.listKey.listId),
        this.platformService.platformType,
      )
      .subscribe(viewModel => {
        this.setViewModelAndHotkeyData(viewModel);
      });
  }

  setViewModelAndHotkeyData(viewModel: MasterListViewModel) {
    this.viewModel = viewModel;
    this.hotKeyService.setItemHeights(this.viewModel?.itemHeights);
    this.hotKeyService.setProductCardIds(this.viewModel?.productCardIds);
    this.hotKeyService.setListId(this.viewModel?.list?.listId?.toString());
  }

  trackItem(index: any, item: any) {
    return item ? index : undefined;
  }

  search() {
    this.collapseAllAlternates();
    if (this.viewModel?.selectedProducts?.size) {
      this.resetAlternateComparison();
    }
    this.masterListService.searchInMasterList(this.searchKey);
  }

  async resetSearch() {
    this.clearFocus();
    setTimeout(this.searchTimeout, 50);
  }

  searchTimeout = () => {
    this.searchKey = '';
    this.search();
  };

  goToTop() {
    this.platformService?.platformType !==
    this.platformService.platformEnum.mobile
      ? this.virtualScroll.scrollToIndex(0)
      : this.virtualScroll.scrollToIndex(0, 'smooth');
  }

  quantityFocusHandler(input: IonInput) {
    this.currentQuantityInput = input;
  }

  changeQuantityHandler(event: any) {
    this.listAnalyticsHelperService.masterListAddToOrder(
      event.quantityUpdate,
      event.product,
    );
  }

  clearFocus() {
    if (this.currentQuantityInput) {
      this.currentQuantityInput.getInputElement().then(element => {
        element.blur();
      });
      this.currentQuantityInput = undefined;
    }
  }

  async openSortAndFilterListModal() {
    const ret = this.listsOverlayService.openSortAndFilterListModal(
      this.viewModel?.groupNames,
      this.viewModel?.productClasses,
      this.viewModel?.listSearchSortFilterOptions?.sortType,
      this.viewModel?.listSearchSortFilterOptions?.filters,
      this.viewModel?.listSearchSortFilterOptions?.groupToSort,
      false,
      true,
    );

    (await ret).onDidDismiss().then(res => {
      if (res.data) {
        const sortData = res as any;
        this.collapseAllAlternates();
        this.resetAlternateComparison();
        this.masterListService.sortAndFilterInMasterList(
          sortData.data.sortMethod,
          sortData.data.groupToSort,
          sortData.data.filters,
        );
        this.listDetailManagementService.updateSortAndFilterUserPreferences(
          ListConstants.masterListTypeId + '-' + this.viewModel?.list?.listId,
          sortData.data.sortMethod,
          sortData.data.groupToSort,
          sortData.data.filters,
        );
      }
    });
  }

  routeTo(path: string, queryParams = {}) {
    this.router.navigate([path], { queryParams, relativeTo: this.route });
  }

  async routeToPDP(productClickData: ProductClickData) {
    if (productClickData.betterBuyCard) {
      this.masterListProductCardService.routeToPDPBetterBuyCard(
        productClickData.productNumber,
        productClickData.currentProduct,
      );
    } else {
      this.masterListProductCardService.routeToPDP(
        productClickData.productNumber,
        productClickData.isQuickView,
      );
    }
  }

  openListOptions(event: Event) {
    this.masterListService.openListOptions(
      event,
      this.viewModel?.items,
      this.viewModel?.showingAllSecondaries,
      this.viewModel?.list,
      this.viewModel?.selectedCustomer,
      this.viewModel?.downloadPrintFlag,
      this.viewModel?.secondariesExist,
      this.listDownloadModal,
    );
  }

  async openProductCardEllipsis(ellipsisClickData: EllipsisClickData) {
    ellipsisClickData.masterList = this.viewModel.list;
    ellipsisClickData.mslCompareAlternatesFeatureFlag =
      await this.panAppState.featureEnabled(
        FEATURES.split_global_msl_compare_alternates.name,
      );
    const buttons = this.masterListProductCardService.constructEllipsisButtons(
      ellipsisClickData,
      this?.viewModel?.selectedCustomer?.restrictToOG === 'M',
    );
    this.masterListProductCardService.openProductCardEllipsis(
      buttons,
      ellipsisClickData.isTouch,
      ellipsisClickData.event,
    );
  }

  async openQuickView(ellipsisClickData: EllipsisClickData) {
    this.masterListProductCardService.openQuickViewModal(
      ellipsisClickData,
      'quickView',
    );
  }

  async clickPromotions(event: EllipsisClickData) {
    this.masterListProductCardService.openQuickViewModal(
      event,
      'productStatus',
    );
  }

  async openProductStatus(ellipsisClickData: EllipsisClickData) {
    this.masterListProductCardService.showProductStatuses(ellipsisClickData);
  }

  collapseAllAlternates() {
    const masterListItemProductKeys =
      this.masterListService.findAllListItemProductKeysWithSecondaries(
        this.viewModel?.items,
      );
    this.masterListService.setShowAlternates(masterListItemProductKeys, false);
  }

  collapseAlternates(item: ProductRow) {
    const masterListItemProductKey = createMasterListItemProductKey(
      item.masterListItem.listId,
      item.masterListItem.listGroupId,
      item.masterListItem.listItemId,
      item.masterListItem.listItemProductId,
    );
    this.masterListService.setShowAlternates([masterListItemProductKey], false);
  }

  ionViewWillLeave() {
    this.handleSubscriptions();
    if (!this.isRedirectingToComparePage) {
      this.resetAllSecondaries();
      this.resetSearch();
      this.resetSortAndFilter();
      this.resetAlternateComparison();
    } else {
      this.masterListService.resetCompareWithAlternatesSelections();

      const masterListItemProductKeys =
        this.masterListService.findAllListItemProductKeysWithSecondaries(
          this.viewModel?.items,
        );
      this.masterListService.resetMasterListOnEnter(
        masterListItemProductKeys,
        this.listKey?.listTypeId + '-' + this.listKey?.listId,
      );
    }
  }

  handleSubscriptions() {
    this.currentQuantityInput = undefined;
    this.masterListViewModelSub$.unsubscribe();
    this.virtualScrollSub$.unsubscribe();
  }

  resetAllSecondaries() {
    const masterListItemProductKeys =
      this.masterListService.findAllListItemProductKeysWithSecondaries(
        this.viewModel?.items,
      );
    this.masterListService.setShowAlternates(masterListItemProductKeys, false);
  }

  resetAlternateComparison() {
    this.masterListService.cancelCompareWithAlternatesMode();
  }

  resetSortAndFilter() {
    this.masterListService.sortAndFilterInMasterList(
      ListConstants.groupLine,
      ListConstants.allGroups,
      [],
    );
  }

  handleBetterBuyDeclineClick(betterBuyDeclineEvent: BetterBuyDeclineEvent) {
    this.betterBuysService.openDeclineModal(betterBuyDeclineEvent);
  }
  acceptSwap(product: Product) {
    this.betterBuysService.openAcceptSwapModal(
      product,
      false,
      BetterBuysConstants.listsCapability,
    );
  }

  closeDownloadListModal() {
    this.listsOverlayService.closeDownloadListModal();
  }

  downloadList(options: DownloadListOptions) {
    this.listsOverlayService.closeDownloadListModal();
    this.masterListService.downloadMasterList(options);
  }

  async openItemNoteModal(ellipsisClickData: EllipsisClickData) {
    this.masterListProductCardService.directlyOpenViewNote(ellipsisClickData);
  }

  async openDownloadPrintModal(download: boolean) {
    const listName = this.viewModel.list?.listName;
    if (download) {
      this.listsOverlayService.openDownloadListModal(
        this.viewModel.list.listId,
        'ML',
        listName,
        this.listDownloadModal,
      );
    } else {
      this.listsOverlayService.openPrintListModal(
        this.viewModel.list.listId,
        'ML',
        listName,
        this.viewModel.selectedCustomer.customerNumber,
        '',
        this.listPrintModal,
      );
    }
  }

  handleOpenSortAndFilterListModal(event: any) {
    this.openSortAndFilterListModal();
  }

  handleClearSortAndFilterListModalEmitter(event: any) {
    this.resetSortAndFilter();

    this.listDetailManagementService.updateSortAndFilterUserPreferences(
      ListConstants.masterListTypeId + '-' + this.viewModel?.list?.listId,
      ListConstants.groupLine,
      ListConstants.allGroups,
      [],
    );
  }

  handleProductSelection(event: any) {
    const key = createMasterListItemProductKey(
      event.masterListItem?.listId,
      event.masterListItem?.listGroupId,
      event.masterListItem?.listItemId,
      event.masterListItem?.listItemProductId,
    );
    this.masterListService.handleMasterListItemSelection(key, event.value);
  }

  clearSelectedNonPrimaryProducts() {
    [...this.viewModel?.selectedProducts?.values()]
      ?.filter(row => !row.isPrimaryProduct && !!row.masterListItem)
      .forEach(row => {
        const key = createMasterListItemProductKey(
          row.masterListItem?.listId,
          row.masterListItem?.listGroupId,
          row.masterListItem?.listItemId,
          row.masterListItem?.listItemProductId,
        );
        this.masterListService.handleMasterListItemSelection(key, false);
      });
  }

  compareSelectedProducts() {
    const selectedPrimaryProduct = [
      ...this.viewModel?.selectedProducts?.values(),
    ]?.find(row => row.isPrimaryProduct);
    if (
      !!selectedPrimaryProduct &&
      this.viewModel?.selectedProducts?.size >= 2
    ) {
      this.isRedirectingToComparePage = true;
      this.masterListProductCardService.routeToProductCompare(
        selectedPrimaryProduct.productNumber,
        [...this.viewModel?.selectedProducts?.values()]?.map(
          row => row.productNumber,
        ),
        ProductCompareEnum.masterListCompare,
      );
    }
  }

  productConversionEmitter(product: Product) {
    this.listProductCardService.handleProductConversionClick(product, false);
  }
}
